import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuthStore } from '../../store/authStore';
import {
  PencilIcon,
  DocumentArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentDuplicateIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const STATUS_CONFIG = {
  draft: { label: 'Taslak', color: 'bg-gray-100 text-gray-700', border: 'border-gray-300' },
  pending: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-700', border: 'border-yellow-300' },
  sent: { label: 'Gönderildi', color: 'bg-blue-100 text-blue-700', border: 'border-blue-300' },
  accepted: { label: 'Kabul Edildi', color: 'bg-green-100 text-green-700', border: 'border-green-300' },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-700', border: 'border-red-300' },
  cancelled: { label: 'İptal Edildi', color: 'bg-gray-200 text-gray-500', border: 'border-gray-300' },
};

const fmt = (v) => parseFloat(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [statusModal, setStatusModal] = useState(null);
  const [statusNote, setStatusNote] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  const { data: quoteData, isLoading } = useQuery({
    queryKey: ['quote', id],
    queryFn: async () => (await api.get(`/quotes/${id}`)).data.data,
  });

  const statusMutation = useMutation({
    mutationFn: async ({ status, status_note }) => {
      return api.patch(`/quotes/${id}/status`, { status, status_note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['quote', id]);
      queryClient.invalidateQueries(['quotes']);
      setStatusModal(null);
      setStatusNote('');
    },
  });

  const handleCopy = async () => {
    if (!quoteData) return;
    const q = quoteData;
    navigate('/quotes/new', {
      state: {
        prefill: {
          originCity: q.origin_city, originDistrict: q.origin_district,
          destCity: q.destination_city, destDistrict: q.destination_district,
        }
      }
    });
  };

  const handleDownloadPdf = () => {
    const token = JSON.parse(localStorage.getItem('emt-crm-auth') || '{}')?.state?.session?.access_token;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    window.open(`${apiUrl}/quotes/${id}/pdf?token=${token}`, '_blank');
  };

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/quotes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['quotes']);
      navigate('/quotes');
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteConfirm(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-medical-600" />
      </div>
    );
  }

  if (!quoteData) {
    return <div className="text-center py-12 text-gray-500">Teklif bulunamadı</div>;
  }

  const q = quoteData;
  const st = STATUS_CONFIG[q.status] || STATUS_CONFIG.draft;
  const effectiveToll = q.toll_fee_override != null ? q.toll_fee_override : q.toll_fee;
  const history = q.history || [];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/quotes')} className="text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{q.quote_number}</h1>
            <p className="text-sm text-gray-500">
              {new Date(q.created_at).toLocaleDateString('tr-TR')} tarihinde oluşturuldu
              {q.creator && ` - ${q.creator.first_name} ${q.creator.last_name}`}
            </p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${st.color}`}>
            {st.label}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <DocumentDuplicateIcon className="h-4 w-4 mr-1" /> Kopyala
          </Button>
          <Link to={`/quotes/${id}/edit`}>
            <Button variant="outline" size="sm">
              <PencilIcon className="h-4 w-4 mr-1" /> Düzenle
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
            <DocumentArrowDownIcon className="h-4 w-4 mr-1" /> PDF İndir
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => setShowDeleteConfirm(true)}>
              <TrashIcon className="h-4 w-4 mr-1" /> Sil
            </Button>
          )}
        </div>
      </div>

      {/* Route & Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <MapPinIcon className="h-5 w-5 text-red-400" />
          <span className="font-semibold">{q.origin_city}/{q.origin_district}</span>
          <ArrowRightIcon className="h-4 w-4" />
          <span className="font-semibold">{q.destination_city}/{q.destination_district}</span>
          <span className="text-sm text-gray-500 ml-2">({q.distance_km} km)</span>
        </div>

        {/* Pricing table */}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#1F4E79] text-white">
                <th className="text-left p-3">Kalem</th>
                <th className="text-right p-3">Detay</th>
                <th className="text-right p-3">Tutar</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="p-3">Mesafe Ücreti</td>
                <td className="text-right p-3 text-gray-500">{q.distance_km} km × {fmt(q.price_per_km)} TL</td>
                <td className="text-right p-3 font-medium">{fmt(q.km_cost)} TL</td>
              </tr>
              <tr className="border-b bg-gray-50">
                <td className="p-3">Ambulans Açılış Ücreti</td>
                <td className="text-right p-3"></td>
                <td className="text-right p-3 font-medium">{fmt(q.ambulance_opening_fee)} TL</td>
              </tr>
              <tr className="border-b">
                <td className="p-3">Yol Harcırahı</td>
                <td className="text-right p-3 text-gray-500">{q.distance_km} km × {fmt(q.road_allowance_per_km)} TL</td>
                <td className="text-right p-3 font-medium">{fmt(q.road_allowance)} TL</td>
              </tr>
              <tr className="border-b bg-gray-50">
                <td className="p-3">Ücretli Yol (Gidiş-Dönüş)</td>
                <td className="text-right p-3 text-gray-500">{fmt(effectiveToll)} TL × 2</td>
                <td className="text-right p-3 font-medium">{fmt(q.toll_cost)} TL</td>
              </tr>
              <tr className="border-t-2 font-semibold">
                <td className="p-3">Ara Toplam</td>
                <td></td>
                <td className="text-right p-3">{fmt(q.subtotal)} TL</td>
              </tr>
              <tr className="border-b">
                <td className="p-3">KDV (%{Math.round(parseFloat(q.kdv_rate) * 100)})</td>
                <td></td>
                <td className="text-right p-3">{fmt(q.kdv_amount)} TL</td>
              </tr>
              <tr className="border-t-2 font-bold text-base">
                <td className="p-3">Genel Toplam</td>
                <td></td>
                <td className="text-right p-3">{fmt(q.total_amount)} TL</td>
              </tr>
              {q.discount_enabled && parseFloat(q.discount_rate) > 0 && (
                <>
                  <tr className="text-red-600">
                    <td className="p-3">İskonto (%{q.discount_rate})</td>
                    <td></td>
                    <td className="text-right p-3">-{fmt(q.discount_amount)} TL</td>
                  </tr>
                </>
              )}
              <tr className="bg-[#1F4E79] text-white font-bold text-lg">
                <td className="p-3">Ödenecek Tutar</td>
                <td></td>
                <td className="text-right p-3">{fmt(q.final_amount)} TL</td>
              </tr>
            </tbody>
          </table>
        </div>

        {q.discount_enabled && q.discount_note && (
          <div className="mt-3 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
            <strong>İskonto Notu (dahili):</strong> {q.discount_note}
          </div>
        )}
      </div>

      {/* Patient & Institution Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Patient */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-3">Hasta Bilgileri</h3>
          {q.patient_name || q.patient_surname ? (
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-500">Ad Soyad:</span> {q.patient_name} {q.patient_surname}</p>
              {q.patient_tc && <p><span className="text-gray-500">TC/Pasaport:</span> {q.patient_tc}</p>}
              {q.patient_phone && <p><span className="text-gray-500">Telefon:</span> {q.patient_phone}</p>}
              {q.patient_age && <p><span className="text-gray-500">Yaş:</span> {q.patient_age}</p>}
              {q.patient_diagnosis && <p><span className="text-gray-500">Tanı:</span> {q.patient_diagnosis}</p>}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Bilgi girilmemiş</p>
          )}
        </div>

        {/* Institution */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-3">Kurum Bilgileri</h3>
          {q.institution_name ? (
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-500">Kurum:</span> {q.institution_name}</p>
              {q.institution_contact && <p><span className="text-gray-500">Yetkili:</span> {q.institution_contact}</p>}
              {q.institution_phone && <p><span className="text-gray-500">Telefon:</span> {q.institution_phone}</p>}
              {q.institution_address && <p><span className="text-gray-500">Adres:</span> {q.institution_address}</p>}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Bilgi girilmemiş</p>
          )}
        </div>
      </div>

      {q.notes && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-2">Notlar</h3>
          <p className="text-sm text-gray-600">{q.notes}</p>
        </div>
      )}

      {/* Status Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Durum İşlemleri</h3>
        <div className="flex flex-wrap gap-2">
          {q.status === 'draft' && (
            <>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => statusMutation.mutate({ status: 'pending' })}>
                Beklemede
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => statusMutation.mutate({ status: 'sent' })}>
                Gönderildi
              </Button>
            </>
          )}
          {(q.status === 'pending' || q.status === 'sent') && (
            <>
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => setStatusModal('accepted')}>
                <CheckCircleIcon className="h-4 w-4 mr-1" /> Kabul Edildi
              </Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setStatusModal('rejected')}>
                <XCircleIcon className="h-4 w-4 mr-1" /> Reddedildi
              </Button>
            </>
          )}
          {q.status !== 'cancelled' && q.status !== 'accepted' && (
            <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => setStatusModal('cancelled')}>
              İptal Et
            </Button>
          )}
          {q.status === 'rejected' && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => statusMutation.mutate({ status: 'draft' })}>
              Taslağa Çevir
            </Button>
          )}
        </div>
      </div>

      {/* Status Change Modal */}
      {statusModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">
              Durum: {STATUS_CONFIG[statusModal]?.label}
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama (Opsiyonel)</label>
              <textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-medical-500"
                placeholder="Durum değişikliği ile ilgili not..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setStatusModal(null); setStatusNote(''); }}>İptal</Button>
              <Button
                onClick={() => statusMutation.mutate({ status: statusModal, status_note: statusNote })}
                disabled={statusMutation.isLoading}
                className="bg-medical-600 hover:bg-medical-700 text-white"
              >
                Onayla
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History Timeline */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Düzenleme Geçmişi</h3>
        {history.length === 0 ? (
          <p className="text-sm text-gray-400">Henüz geçmiş kaydı yok</p>
        ) : (
          <div className="space-y-4">
            {history.map((h) => (
              <div key={h.id} className="flex gap-3">
                <div className="flex-shrink-0 mt-1">
                  <ClockIcon className="h-4 w-4 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-700">
                      {h.changer ? `${h.changer.first_name} ${h.changer.last_name}` : 'Sistem'}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(h.changed_at).toLocaleString('tr-TR')}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      h.action === 'created' ? 'bg-green-100 text-green-700' :
                      h.action === 'status_changed' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {h.action === 'created' ? 'Oluşturuldu' :
                       h.action === 'status_changed' ? 'Durum Değişti' :
                       h.action === 'updated' ? 'Güncellendi' : h.action}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{h.description}</p>
                  {h.field_name && h.action === 'updated' && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {h.field_name}: {h.old_value} → {h.new_value}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Teklifi Sil</h3>
            <p className="text-sm text-gray-600 mb-1">
              <strong>{q.quote_number}</strong> numaralı teklifi silmek istediğinize emin misiniz?
            </p>
            <p className="text-xs text-red-500 mb-4">Bu işlem geri alınamaz.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                İptal
              </Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete} disabled={deleteMutation.isLoading}>
                {deleteMutation.isLoading ? 'Siliniyor...' : 'Evet, Sil'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
